import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer, MapPin, Calendar, Users, Car, CreditCard, Mail, Phone, User, ArrowRight, ShieldCheck, Clock, Compass } from "lucide-react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import logoUrl from "@/assets/allen-travels-logo.png";
import { useSeo } from "@/hooks/useSeo";

const PublicVoucher = () => {
  useSeo({ robots: "noindex,follow", title: "Voucher | Allen Travels RD" });
  const { bookingCode } = useParams<{ bookingCode: string }>();
  const [booking, setBooking] = useState<any>(null);
  const [isTour, setIsTour] = useState(false);
  const [vehicle, setVehicle] = useState<{ name: string } | null>(null);
  const [tourInfo, setTourInfo] = useState<{ title: string; destination: string; duration: string } | null>(null);
  const [packageName, setPackageName] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingCode) { setNotFound(true); setLoading(false); return; }

      const isTourBooking = bookingCode.startsWith("TRB-");
      setIsTour(isTourBooking);

      if (isTourBooking) {
        const { data, error } = await supabase
          .from("tour_bookings")
          .select("*, tours(title, duration, destinations(title))")
          .eq("booking_code", bookingCode)
          .single();

        if (error || !data) {
          setNotFound(true);
        } else {
          setBooking(data);
          setTourInfo({
            title: (data as any).tours?.title || "Tour",
            destination: (data as any).tours?.destinations?.title || "",
            duration: (data as any).tours?.duration || "",
          });
          if (data.tour_package_id) {
            const { data: pkg } = await supabase
              .from("tour_packages")
              .select("name")
              .eq("id", data.tour_package_id)
              .single();
            setPackageName(pkg?.name || "");
          }
        }
      } else {
        const { data, error } = await supabase
          .from("bookings")
          .select("*, vehicles(name)")
          .eq("booking_code", bookingCode)
          .single();

        if (error || !data) {
          setNotFound(true);
        } else {
          setBooking(data);
          setVehicle((data as any).vehicles || { name: "N/A" });
        }
      }
      setLoading(false);
    };
    fetchBooking();
  }, [bookingCode]);

  const paymentMethodLabels: Record<string, string> = {
    cash: "Cash",
    paypal: "PayPal",
    stripe: "Credit / Debit Card",
  };

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
    confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
    completed: "bg-green-500/15 text-green-700 border-green-200",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-soft">
        <header className="bg-hero-bg py-4 border-b border-hero-foreground/10">
          <div className="container mx-auto px-4">
            <Skeleton className="h-8 w-32" />
          </div>
        </header>
        <div className="container mx-auto px-4 py-8 max-w-3xl space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-soft flex items-center justify-center px-4">
        <div className="text-center space-y-4 glass shadow-card rounded-2xl p-10 max-w-md">
          <ShieldCheck className="w-16 h-16 text-destructive mx-auto opacity-60" />
          <h1 className="font-display text-2xl font-bold text-foreground">Booking Not Found</h1>
          <p className="text-muted-foreground font-sans">The voucher code is invalid or does not exist.</p>
          <Button onClick={() => window.location.href = "/"} className="bg-gradient-ocean text-white">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft">
      <header className="bg-hero-bg py-4 border-b border-hero-foreground/10 print:hidden">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <a href="/" className="inline-block">
            <img src={logoUrl} alt="Allen Travels RD" className="h-12 w-auto" />
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Verified Badge */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-ocean shadow-elegant mb-5">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">
            <span className="text-gradient-ocean">Verified</span> {isTour ? "Tour " : ""}Booking
          </h1>
          <p className="text-muted-foreground font-sans mt-2">This voucher has been verified in our system.</p>
        </div>

        {/* Voucher Card */}
        <div className="glass shadow-elegant rounded-2xl overflow-hidden print:shadow-none print:border-2 print:border-border">
          <div className="bg-gradient-ocean px-6 py-6 text-center">
            <p className="text-white/70 font-sans text-xs uppercase tracking-[0.22em] mb-1">Booking Code</p>
            <p className="text-3xl md:text-4xl font-display font-bold text-white tracking-wider">{booking.booking_code}</p>
          </div>

          <div className="p-6 space-y-6">
            {isTour ? (
              <TourVoucherDetails booking={booking} tourInfo={tourInfo!} packageName={packageName} />
            ) : (
              <TransferVoucherDetails booking={booking} vehicle={vehicle} />
            )}

            <hr className="border-border" />

            {/* Customer Info */}
            <div className="space-y-3">
              <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_phone}</span>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Payment & Total */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="font-sans text-sm text-muted-foreground">Payment:</span>
                  <span className="font-sans text-sm font-medium text-foreground">
                    {paymentMethodLabels[booking.payment_method] || booking.payment_method}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusColors[booking.payment_status] || statusColors.pending}>
                    {booking.payment_status === "paid" ? "Paid" : booking.payment_status === "pending" ? "Pending" : "Unpaid"}
                  </Badge>
                  <Badge variant="outline" className={statusColors[booking.status] || statusColors.pending}>
                    {booking.status === "pending" ? "Pending" : booking.status}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Total</p>
                <p className="text-3xl md:text-4xl font-display font-bold text-gradient-ocean">${booking.total_price}</p>
              </div>
            </div>

            {/* Comments / Special Requests */}
            {(isTour ? booking.comments : booking.special_requests) && (
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-xs text-muted-foreground font-sans uppercase tracking-widest mb-1">
                  {isTour ? "Comments" : "Special Requests"}
                </p>
                <p className="font-sans text-sm text-foreground">
                  {isTour ? booking.comments : booking.special_requests}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Print button */}
        <div className="flex justify-center mt-6 print:hidden">
          <Button onClick={() => window.print()} variant="outline" className="font-sans h-11">
            <Printer className="w-4 h-4 mr-2" /> Print Voucher
          </Button>
        </div>
      </div>
    </div>
  );
};

function TourVoucherDetails({ booking, tourInfo, packageName }: { booking: any; tourInfo: { title: string; destination: string; duration: string }; packageName: string }) {
  return (
    <>
      {/* Tour Info */}
      <div className="space-y-3">
        <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Tour Details</h3>
        <div className="flex items-start gap-3">
          <Compass className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="font-sans text-sm font-medium text-foreground">{tourInfo.title}</p>
            {tourInfo.destination && (
              <p className="text-xs text-muted-foreground">{tourInfo.destination}</p>
            )}
          </div>
        </div>
      </div>

      {/* Date & Schedule */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Excursion Date</p>
            <p className="font-sans text-sm font-medium text-foreground">
              {booking.excursion_date ? format(new Date(booking.excursion_date + "T12:00:00"), "PPP") : "N/A"}
            </p>
          </div>
        </div>
        {booking.schedule && (
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground font-sans">Schedule</p>
              <p className="font-sans text-sm font-medium text-foreground">{booking.schedule}</p>
            </div>
          </div>
        )}
      </div>

      {/* Participants */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {booking.pricing_mode === "fixed" ? (
          <>
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground font-sans">Adults</p>
                <p className="font-sans text-sm font-medium text-foreground">{booking.adults}</p>
              </div>
            </div>
            {booking.children > 0 && (
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground font-sans">Children</p>
                  <p className="font-sans text-sm font-medium text-foreground">{booking.children}</p>
                </div>
              </div>
            )}
          </>
        ) : packageName ? (
          <div className="flex items-center gap-3">
            <Compass className="w-4 h-4 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground font-sans">Package</p>
              <p className="font-sans text-sm font-medium text-foreground">{packageName}</p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Pickup Point */}
      {booking.pickup_point && (
        <div className="flex items-center gap-3">
          <MapPin className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Pickup Point</p>
            <p className="font-sans text-sm font-medium text-foreground">{booking.pickup_point}</p>
          </div>
        </div>
      )}

      {/* Duration */}
      {tourInfo.duration && (
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Duration</p>
            <p className="font-sans text-sm font-medium text-foreground">{tourInfo.duration}</p>
          </div>
        </div>
      )}
    </>
  );
}

function TransferVoucherDetails({ booking, vehicle }: { booking: any; vehicle: { name: string } | null }) {
  return (
    <>
      {/* Route */}
      <div className="space-y-3">
        <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Route Details</h3>
        <div className="flex items-start gap-3">
          <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-sans text-sm font-medium text-foreground">{booking.pickup_location}</p>
            <div className="flex items-center gap-2 my-1">
              <ArrowRight className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-sans">
                {booking.route_type === "round-trip" ? "Round Trip" : "One Way"}
                {booking.distance_km && ` • ${booking.distance_km} km`}
                {booking.estimated_time_min && ` • ~${booking.estimated_time_min} min`}
              </span>
            </div>
            <p className="font-sans text-sm font-medium text-foreground">{booking.dropoff_location}</p>
          </div>
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Pickup Date</p>
            <p className="font-sans text-sm font-medium text-foreground">
              {booking.pickup_date ? formatInTimeZone(new Date(booking.pickup_date), "UTC", "PPP 'at' p") : "N/A"}
            </p>
          </div>
        </div>
        {booking.return_date && (
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground font-sans">Return Date</p>
              <p className="font-sans text-sm font-medium text-foreground">
                {(() => { try { return formatInTimeZone(new Date(booking.return_date), "UTC", "PPP 'at' p"); } catch { return "N/A"; } })()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Vehicle & Passengers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <Car className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Vehicle</p>
            <p className="font-sans text-sm font-medium text-foreground">{vehicle?.name || "N/A"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Users className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Passengers</p>
            <p className="font-sans text-sm font-medium text-foreground">{booking.passengers}</p>
          </div>
        </div>
      </div>

      {/* Flight info */}
      {booking.flight_number && (
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground font-sans">Flight</p>
            <p className="font-sans text-sm font-medium text-foreground">{booking.flight_number}</p>
          </div>
        </div>
      )}
    </>
  );
}

export default PublicVoucher;
